"use client"

import { ToastType, useToast } from "@/app/context/ToastContext"
import { sendSlackMessage } from "@/app/helpers/actions/sendSlackReply"
import { useRef, useState } from "react"
import styles from "./ChatReplyInput.module.css"
import clsx from "clsx"
import { Icon, IconName, IconSize } from "../../icons/Icon"

export default function ChatReplyInput({
  channelId,
  threadTs,
  markTaskAsDone,
}: {
  channelId: string
  threadTs: string
  markTaskAsDone: () => void
}) {
  const [replyText, setReplyText] = useState("")
  const [sending, setSending] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const { showToast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (sending) return
    if (replyText.trim()) {
      setSending(true)
      try {
        const result = await sendSlackMessage(
          channelId,
          threadTs,
          replyText.trim(),
        )
        if (result.error) {
          console.error("Error sending reply:", result.error)
        } else {
          setReplyText("")
        }
      } catch (error) {
        showToast(
          "Your message couldn’t be sent. Please try again.",
          ToastType.Error,
        )
        console.error("Error sending reply:", error)
      } finally {
        setSending(false)
      }
    }
    markTaskAsDone()
  }

  const isInputFieldEmpty = !replyText.trim()

  return (
    <div className={styles.chatReplyInputContainer}>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={styles.chatReplyInput}
      >
        <input
          name="replyText"
          type="text"
          placeholder="Write response ..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          disabled={sending}
          autoComplete="off"
          className={styles.chatReplyInputText}
        />
        <button
          type="button"
          disabled={isInputFieldEmpty || sending}
          aria-hidden={isInputFieldEmpty && !sending}
          className={clsx(
            styles.chatReplyInputButton,
            isInputFieldEmpty && !sending && styles.chatReplyInputButtonHidden,
            !isInputFieldEmpty && styles.chatReplyInputButtonVisible,
            sending && styles.chatReplyInputButtonVisible,
            isInputFieldEmpty || sending
              ? styles.chatReplyInputButtonDisabled
              : styles.chatReplyInputButtonEnabled,
          )}
          onClick={(e) => {
            e.preventDefault()
            if (isInputFieldEmpty || sending) return
            formRef.current?.requestSubmit()
          }}
        >
          <div
            className={sending ? styles.chatReplyInputButtonIconSending : ""}
          >
            <Icon
              name={sending ? IconName.Loader : IconName.ArrowRight}
              size={IconSize.Small}
              customColor={isInputFieldEmpty || sending ? "#8F8F8F" : "#FFF"}
            />
          </div>
        </button>
      </form>
    </div>
  )
}
